import { motion, useAnimate } from 'framer-motion'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { EmptyHex } from 'assets/game/empty-hex'

import { getOrbComponent, getPieceComponent, isMyPiece } from 'features/board/utils'
import { useGameEventContext } from 'features/game/event-context'
import { useGameContext } from 'features/game/game-context'
import { switchRestrictions } from 'features/game/utils'
import { HexId, HexPiece } from 'features/hex-board/hex-board'
import { getHexPosition } from 'features/hex-board/utils'

import { useBoolean } from 'shared/hooks/use-boolean'
import { cn } from 'shared/utils'

interface IHex {
  hexId: HexId
  piece: HexPiece
  potentialMove: boolean
  potentiallyDies: boolean
}

export const Hex: FC<IHex> = ({ hexId, piece, potentialMove, potentiallyDies }) => {
  const { hexSelected, handleHexClick } = useGameEventContext()
  const {
    game,
    phase,
    relativeGameData: { playerColor, turn },
    lastMove,
    updateBoardState,
  } = useGameContext()
  const [scope, animate] = useAnimate()
  const [isAnimating, toggleIsAnimating] = useBoolean()
  const previousMoveRef = useRef(lastMove)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const { x, y } = getHexPosition({ playerColor: playerColor.bottom, hexId })
  const isDisabled = useMemo(
    () => hexSelected && switchRestrictions(game.events, [hexSelected, hexId]),
    [hexSelected, game.events, hexId]
  )
  const canScale = useMemo(() => phase === 'fight' && !isAnimating, [phase, isAnimating])
  const isSelected = useMemo(() => canScale && hexSelected === hexId, [canScale, hexSelected, hexId])
  const isHover = useMemo(
    () => canScale && turn.bottom && isMyPiece(playerColor.bottom, piece),
    [playerColor.bottom, canScale, turn.bottom, piece]
  )

  const PieceComponent = useMemo(() => getPieceComponent(piece), [piece])
  const OrbComponent = useMemo(() => getOrbComponent(piece, cn(isDisabled && 'opacity-50')), [piece, isDisabled])

  useEffect(() => {
    if (!lastMove || lastMove === previousMoveRef.current) return
    previousMoveRef.current = lastMove

    const animateMove = async (toX: number, toY: number) => {
      toggleIsAnimating(true)
      await animate(
        scope.current,
        {
          x: toX - x,
          y: toY - y,
          scale: [1, 1],
        },
        { duration: 0.2, ease: 'easeInOut' }
      )
      updateBoardState.current()
      animate(
        scope.current,
        {
          x: 0,
          y: 0,
          scale: 1,
        },
        { duration: 0 }
      )
      toggleIsAnimating(false)
    }

    if (lastMove.type === 'move' && lastMove.metadata.from === hexId) {
      const { to } = lastMove.metadata
      const { x: toX, y: toY } = getHexPosition({ playerColor: playerColor.bottom, hexId: to })
      animateMove(toX, toY)
    }

    if (lastMove.type === 'fight' && potentiallyDies) {
      animate(scope.current, { opacity: [1, 0, 1, 0] }, { duration: 0.8 }).then(() =>
        animate(scope.current, { opacity: 1 }, { duration: 0 })
      )
    }

    if (lastMove.type === 'switch') {
      const { from, to } = lastMove.metadata
      if (from === hexId || to === hexId) {
        const { x: toX, y: toY } = getHexPosition({
          playerColor: playerColor.bottom,
          hexId: from === hexId ? to : from,
        })
        animateMove(toX, toY)
      }
    }

    if (lastMove.type === 'deploy' || lastMove.type === 'distribute') {
      const to = lastMove.metadata.hexId
      if (to === hexId) {
        setShouldAnimate(true)
        updateBoardState.current()
      }
    }

    if (lastMove.type === 'withdraw' && lastMove.metadata.hexId === hexId) {
      let y = -800
      if (lastMove.metadata.player === playerColor.bottom) {
        y = 800
      }
      animate(
        scope.current,
        {
          x: -x,
          y,
          opacity: 0.5,
          scale: 0.5,
        },
        { duration: 0.3, ease: 'linear' }
      ).then(() => {
        updateBoardState.current()
        animate(scope.current, { x: 0, y: 0, opacity: 1, scale: 1 }, { duration: 0 })
      })
    }
  }, [lastMove, hexId, playerColor.bottom, animate, x, y, updateBoardState, scope, toggleIsAnimating, potentiallyDies])

  useEffect(() => {
    if (shouldAnimate) {
      animate(scope.current, { opacity: [0, 1], y: [30, 0] }, { duration: 0.3, ease: 'easeOut' })
      setShouldAnimate(false)
    }
  }, [shouldAnimate, animate, scope, turn.bottom])

  const pieceComponent = useMemo(
    () =>
      PieceComponent &&
      OrbComponent &&
      !shouldAnimate && (
        <>
          <PieceComponent />
          {OrbComponent}
        </>
      ),
    [PieceComponent, OrbComponent, shouldAnimate]
  )

  return (
    <motion.g
      variants={{ normal: { scale: 1, x, y }, big: { scale: 1.2, x, y } }}
      animate={isSelected ? 'big' : 'normal'}
      whileHover={isHover ? 'big' : 'normal'}
      transition={{ duration: 0.1, type: 'tween' }}
      onClick={() => handleHexClick(hexId)}
    >
      <EmptyHex potentialMove={potentialMove} />
      <motion.g ref={scope} initial={{ opacity: 1 }}>
        {pieceComponent}
      </motion.g>
      {/* <text strokeWidth={5} fontSize={60} x={100} y={160} stroke="black">
        {hexId}
      </text> */}
    </motion.g>
  )
}
